import { z } from "zod";
import type {
	FormRule,
	FormRuleAction,
	FormRuleCondition,
	FormRuleConditionOperatorValue,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
import {
	FormRuleActionType,
	FormRuleConditionLogic,
	FormRuleConditionOperator,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";

export type {
	FormRule,
	FormRuleAction,
	FormRuleActionTypeValue,
	FormRuleCondition,
	FormRuleConditionLogicValue,
	FormRuleConditionOperatorValue,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";
/**
 * Re-export the canonical {@link FormRule} family from the dynamicForms runtime so
 * the form-editor UI imports them through this module and the runtime stays the
 * single source of truth. Zod schemas + UI-only helpers live below.
 */
export {
	FormRuleActionType,
	FormRuleConditionLogic,
	FormRuleConditionOperator,
} from "@/lib/dynamicForms/interfaces/dynamicForm.interface";

/**
 * Value comparison primitive — accepts string/number/boolean and string[] (for `in` / `not_in`).
 */
export const FormRuleValueSchema = z.union([
	z.string(),
	z.number(),
	z.boolean(),
	z.array(z.string()),
]);
export type FormRuleValue = z.infer<typeof FormRuleValueSchema>;

export const FormRuleConditionSchema: z.ZodType<FormRuleCondition> = z.object({
	id: z.string(),
	fieldDefinitionId: z.string(),
	operator: z.enum([
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.notContains,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.greaterThanOrEqual,
		FormRuleConditionOperator.lessThanOrEqual,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
		FormRuleConditionOperator.isChecked,
		FormRuleConditionOperator.isNotChecked,
		FormRuleConditionOperator.in,
		FormRuleConditionOperator.notIn,
	]),
	value: FormRuleValueSchema.optional(),
});

export const FormRuleActionSchema: z.ZodType<FormRuleAction> = z.object({
	id: z.string(),
	type: z.enum([
		FormRuleActionType.showField,
		FormRuleActionType.hideField,
		FormRuleActionType.enableField,
		FormRuleActionType.disableField,
		FormRuleActionType.setRequired,
		FormRuleActionType.setOptional,
		FormRuleActionType.setValidation,
	]),
	targetFieldDefinitionId: z.string(),
	value: FormRuleValueSchema.optional(),
});

export const FormRuleSchema: z.ZodType<FormRule> = z.object({
	id: z.string(),
	name: z.string(),
	description: z.string().optional(),
	enabled: z.boolean().default(true),
	conditionLogic: z.enum([
		FormRuleConditionLogic.all,
		FormRuleConditionLogic.any,
	]),
	conditions: z.array(FormRuleConditionSchema),
	actions: z.array(FormRuleActionSchema).min(1),
	createdAt: z.string().optional(),
	updatedAt: z.string().optional(),
});

export const FormRulesEnvelopeSchema = z.array(FormRuleSchema);

/**
 * Operators allowed for a given persisted field `dataType`. Loose entries (e.g. unknown
 * types) fall back to {@link DEFAULT_FORM_RULE_OPERATORS}.
 *
 * Matches ADO #1187 "Operator Support by Field Type" guidance: text/select get
 * equality + containment, numbers get inequality, checkbox/switch get isChecked/isNotChecked, etc.
 */
export const DEFAULT_FORM_RULE_OPERATORS: readonly FormRuleConditionOperatorValue[] =
	[
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	] as const;

export const FORM_RULE_OPERATORS_BY_DATA_TYPE: Readonly<
	Record<string, readonly FormRuleConditionOperatorValue[]>
> = {
	text: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.notContains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	textarea: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.notContains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	url: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.notContains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	phone: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	email: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	number: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.greaterThanOrEqual,
		FormRuleConditionOperator.lessThanOrEqual,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	integer: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.greaterThanOrEqual,
		FormRuleConditionOperator.lessThanOrEqual,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	decimal: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.greaterThanOrEqual,
		FormRuleConditionOperator.lessThanOrEqual,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	boolean: [
		FormRuleConditionOperator.isChecked,
		FormRuleConditionOperator.isNotChecked,
	],
	checkbox: [
		FormRuleConditionOperator.isChecked,
		FormRuleConditionOperator.isNotChecked,
	],
	switch: [
		FormRuleConditionOperator.isChecked,
		FormRuleConditionOperator.isNotChecked,
	],
	select: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.in,
		FormRuleConditionOperator.notIn,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	radio: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	date: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	time: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	dateTime: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.greaterThan,
		FormRuleConditionOperator.lessThan,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	file: [
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	address: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.contains,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	multiselect: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	users: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
	signature: [
		FormRuleConditionOperator.equals,
		FormRuleConditionOperator.notEquals,
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
	],
};

/**
 * Returns the allowed operator set for a given persisted-field `dataType`,
 * falling back to {@link DEFAULT_FORM_RULE_OPERATORS} when no specific list is defined.
 */
export function getFormRuleOperatorsForDataType(
	dataType: string | undefined | null,
): readonly FormRuleConditionOperatorValue[] {
	if (!dataType) return DEFAULT_FORM_RULE_OPERATORS;
	const normalized = dataType.toLowerCase();
	const exact = FORM_RULE_OPERATORS_BY_DATA_TYPE[dataType];
	if (exact) return exact;
	const normalizedMatch = FORM_RULE_OPERATORS_BY_DATA_TYPE[normalized];
	if (normalizedMatch) return normalizedMatch;
	return DEFAULT_FORM_RULE_OPERATORS;
}

/**
 * Operators that do not require a comparison value (e.g. is_empty, is_checked).
 */
export const FORM_RULE_OPERATORS_WITHOUT_VALUE: ReadonlySet<FormRuleConditionOperatorValue> =
	new Set([
		FormRuleConditionOperator.isEmpty,
		FormRuleConditionOperator.isNotEmpty,
		FormRuleConditionOperator.isChecked,
		FormRuleConditionOperator.isNotChecked,
	]);

/**
 * Operators whose value is a list (rendered as multi-input).
 */
export const FORM_RULE_OPERATORS_WITH_LIST_VALUE: ReadonlySet<FormRuleConditionOperatorValue> =
	new Set([FormRuleConditionOperator.in, FormRuleConditionOperator.notIn]);
